import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, IconButton, Stack, Tooltip, Typography } from "@material-ui/core";
import { WhatsApp as WhatsAppIcon } from "@material-ui/icons";
import {
  buildSigningLinkWhatsappShare,
  type ShareableDocument
} from "../../../utils/signatureWhatsappShare";

const WHATSAPP_GREEN = "#25D366";

export interface SigningLinkWhatsappShareProps {
  /** One document, or every document of a set — the message carries ONE link (the first's) naming them all. */
  documents: ShareableDocument[];
  /** The client, as a signer of the FIRST document. Default: its first signer. */
  signerId?: string;
  disabled?: boolean;
  /** Called when the realtor taps it (the chat opened; whether they pressed send there is unknown). */
  onShared?: () => void;
}

/**
 * "שיתוף בוואטסאפ": opens the REALTOR'S OWN WhatsApp on a chat with the client, with a short
 * message and the signing link(s) typed in (see `utils/signatureWhatsappShare.ts`). A plain link —
 * it sends nothing through Webot, writes nothing and changes no status, so it is safe on any
 * surface that may show the signing link. Renders nothing when there is no link to share.
 *
 * A link rather than `window.open` after a click handler: opened from the user's own tap, the
 * `wa.me` universal link reaches the WhatsApp app on a phone and WhatsApp Web / Desktop on a
 * computer, with no popup blocker in the way.
 */
export default function SigningLinkWhatsappShareButton({
  documents,
  signerId,
  disabled,
  onShared,
  size = "medium",
  fullWidth,
  showHint
}: SigningLinkWhatsappShareProps & {
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  /** A line under the button saying where the message goes — and, once tapped, what happens next. */
  showHint?: boolean;
}) {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const share = buildSigningLinkWhatsappShare(documents, t, signerId);
  if (!share) return null;
  const button = (
    <Button
      component="a"
      href={share.href}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      color="success"
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      startIcon={<WhatsAppIcon sx={{ color: disabled ? undefined : WHATSAPP_GREEN }} />}
      onClick={(event: MouseEvent) => {
        // Rows and cards open the document on click; this only opens WhatsApp.
        event.stopPropagation();
        setOpened(true);
        onShared?.();
      }}
    >
      {t("signatures.whatsappShare.label")}
    </Button>
  );
  if (!showHint) return button;
  return (
    <Stack spacing={0.5}>
      {button}
      <Typography variant="caption" color="text.secondary" aria-live="polite">
        {opened
          ? t("signatures.whatsappShare.opened")
          : !share.recipientDigits
          ? t("signatures.whatsappShare.hintNoPhone")
          : share.documentCount > 1
          ? t("signatures.whatsappShare.hintMany", { total: share.documentCount })
          : t("signatures.whatsappShare.hint")}
      </Typography>
    </Stack>
  );
}

/** The same action as a row icon (the sent-documents list), with the explanation as its tooltip. */
export function SigningLinkWhatsappShareIconButton({ documents, signerId, disabled }: SigningLinkWhatsappShareProps) {
  const { t } = useTranslation();
  const share = buildSigningLinkWhatsappShare(documents, t, signerId);
  if (!share) return null;
  const label = t("signatures.whatsappShare.tooltip") as string;
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          component="a"
          href={share.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          disabled={disabled}
          onClick={(event: MouseEvent) => event.stopPropagation()}
          sx={{ color: WHATSAPP_GREEN }}
        >
          <WhatsAppIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}
